// src/components/ArticlesManager.js
import React, { useState, useEffect } from 'react';
import { FileText, Plus, Edit2, Trash2, Eye, EyeOff, Star, Download } from 'lucide-react';

const ArticlesManager = ({ apiUrl, token }) => {
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ total: 0, published: 0, draft: 0, archived: 0 });
  const [showFranceInfoSync, setShowFranceInfoSync] = useState(false);

  const authenticatedFetch = async (url, options = {}) => {
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  };

  const loadArticles = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await authenticatedFetch(`${apiUrl}/articles?${params}`);
      const data = await response.json();
      setArticles(data.articles);
    } catch (error) {
      console.error('Error loading articles:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await authenticatedFetch(`${apiUrl}/articles/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  useEffect(() => {
    loadArticles();
    loadStats();
  }, [filter, searchTerm]);

  const handleCreate = () => {
    setSelectedArticle({
      title: '',
      excerpt: '',
      content: '',
      image: '',
      author: '',
      category: 'actualite',
      tags: [],
      status: 'draft'
    });
    setEditMode(true);
  };

  const handleSave = async (article) => {
    try {
      const isNew = !article._id;
      const url = isNew 
        ? `${apiUrl}/articles` 
        : `${apiUrl}/articles/${article._id}`;
      
      const method = isNew ? 'POST' : 'PUT';
      
      const response = await authenticatedFetch(url, {
        method,
        body: JSON.stringify(article)
      });

      if (response.ok) {
        const updated = await response.json();
        setSelectedArticle(updated);
        loadArticles();
        loadStats();
        setEditMode(false);
        alert(isNew ? 'Article créé !' : 'Article mis à jour !');
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.error}`);
      }
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handlePublish = async (articleId, publish) => {
    try {
      const response = await authenticatedFetch(`${apiUrl}/articles/${articleId}/publish`, {
        method: 'PATCH',
        body: JSON.stringify({ publish })
      });

      if (response.ok) {
        const updated = await response.json();
        setSelectedArticle(updated);
        loadArticles();
        loadStats();
      }
    } catch (error) {
      console.error('Error publishing article:', error);
    }
  };

  const handleFeature = async (articleId) => {
    try {
      const response = await authenticatedFetch(`${apiUrl}/articles/${articleId}/feature`, {
        method: 'PATCH'
      });

      if (response.ok) {
        const updated = await response.json();
        setSelectedArticle(updated);
        loadArticles();
      }
    } catch (error) {
      console.error('Error featuring article:', error);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${apiUrl}/articles/${articleId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSelectedArticle(null);
        loadArticles();
        loadStats();
        alert('Article supprimé');
      }
    } catch (error) {
      console.error('Error deleting article:', error);
    }
  };

  const syncFranceInfo = async () => {
    if (!window.confirm('Synchroniser les articles de France Info ? Cela peut prendre quelques secondes.')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${apiUrl}/articles/sync-franceinfo`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ ${result.imported} article(s) importé(s), ${result.skipped} ignoré(s)`);
        loadArticles();
        loadStats();
      }
    } catch (error) {
      console.error('Error syncing France Info:', error);
      alert('Erreur lors de la synchronisation');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  return (
    <div className="articles-manager">
      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total</div>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Brouillons</div>
          <div className="stat-value gray">{stats.draft}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Publiés</div>
          <div className="stat-value green">{stats.published}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Vues totales</div>
          <div className="stat-value orange">{stats.totalViews}</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="filters-card">
        <div className="filters-content">
          <div className="search-box">
            <input
              type="text"
              placeholder="Rechercher un article..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-buttons">
            {['all', 'draft', 'published', 'archived'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`filter-btn ${filter === f ? 'active' : ''} ${f}`}
              >
                {f === 'all' ? 'Tous' : f === 'draft' ? 'Brouillons' : f === 'published' ? 'Publiés' : 'Archivés'}
              </button>
            ))}
          </div>
          <div className="export-buttons">
            <button onClick={handleCreate} className="btn-export">
              <Plus className="icon" />
              Nouvel article
            </button>
            <button onClick={() => setShowFranceInfoSync(!showFranceInfoSync)} className="btn-import">
              <Download className="icon" />
              France Info
            </button>
          </div>
        </div>
        
        {showFranceInfoSync && (
          <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <p style={{ marginBottom: '10px' }}>
              <strong>Synchronisation France Info</strong><br />
              Importer automatiquement les derniers articles de France Info.
            </p>
            <button onClick={syncFranceInfo} className="btn-import">
              <Download className="icon" />
              Lancer la synchronisation
            </button>
          </div>
        )}
      </div>

      {/* Liste et détails */}
      <div className="main-grid">
        {/* Liste */}
        <div className="card">
          <div className="card-header">
            <h2>Articles ({articles.length})</h2>
          </div>
          <div className="shops-list">
            {articles.map(article => (
              <div
                key={article._id}
                onClick={() => { setSelectedArticle(article); setEditMode(false); }}
                className={`shop-item ${selectedArticle?._id === article._id ? 'active' : ''}`}
              >
                <div className="shop-item-content">
                  <div className="shop-logo">
                    {article.image ? (
                      <img src={article.image} alt={article.title} />
                    ) : (
                      <div className="shop-logo-placeholder">
                        <FileText className="placeholder-icon" />
                      </div>
                    )}
                  </div>
                  <div className="shop-info">
                    <div className="shop-header">
                      <h3>{article.title}</h3>
                      <span className={`badge ${article.status}`}>
                        {article.status}
                      </span>
                    </div>
                    <p className="shop-category">{article.category}</p>
                    <div className="shop-stats">
                      <span className="shop-stat">{article.views} vues</span>
                      <span className="shop-stat">{formatDate(article.publishedAt)}</span>
                      {article.featured && <span className="shop-stat">⭐ À la une</span>}
                      {article.source === 'franceinfo' && <span className="shop-stat">📰 France Info</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Détails ou édition */}
        {editMode ? (
          <ArticleEditForm
            article={selectedArticle}
            onSave={handleSave}
            onCancel={() => setEditMode(false)}
            apiUrl={apiUrl}
            token={token}
          />
        ) : (
          <ArticleDetails
            article={selectedArticle}
            onEdit={() => setEditMode(true)}
            onPublish={handlePublish}
            onFeature={handleFeature}
            onDelete={handleDelete}
            formatDate={formatDate}
          />
        )}
      </div>
    </div>
  );
};

// Composant d'édition
const ArticleEditForm = ({ article, onSave, onCancel }) => {
  const [editedArticle, setEditedArticle] = useState({ ...article });
  const [newTag, setNewTag] = useState('');

  const addTag = () => {
    if (newTag.trim() && !editedArticle.tags.includes(newTag.trim())) {
      setEditedArticle({
        ...editedArticle,
        tags: [...(editedArticle.tags || []), newTag.trim()]
      });
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setEditedArticle({
      ...editedArticle,
      tags: editedArticle.tags.filter(tag => tag !== tagToRemove)
    });
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>{article._id ? 'Éditer l\'article' : 'Nouvel article'}</h2>
      </div>
      <div className="details-content">
        <div className="edit-form">
          <div className="form-section">
            <h3 className="form-section-title">Informations générales</h3>
            
            <div className="form-group">
              <label>Titre *</label>
              <input
                type="text"
                value={editedArticle.title}
                onChange={(e) => setEditedArticle({...editedArticle, title: e.target.value})}
                placeholder="Titre de l'article"
              />
            </div>

            <div className="form-group">
              <label>Chapeau / Extrait * (max 300 caractères)</label>
              <textarea
                rows="3"
                value={editedArticle.excerpt}
                onChange={(e) => setEditedArticle({...editedArticle, excerpt: e.target.value})}
                placeholder="Résumé court qui apparaîtra dans la liste"
                maxLength="300"
              />
              <span className="form-help">{editedArticle.excerpt?.length || 0}/300</span>
            </div>

            <div className="form-group">
              <label>Contenu * (HTML autorisé)</label>
              <textarea
                rows="15"
                value={editedArticle.content}
                onChange={(e) => setEditedArticle({...editedArticle, content: e.target.value})}
                placeholder="Contenu complet de l'article..."
              />
            </div>

            <div className="form-group">
              <label>Image (URL)</label>
              <input
                type="text"
                value={editedArticle.image || ''}
                onChange={(e) => setEditedArticle({...editedArticle, image: e.target.value})}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Auteur *</label>
                <input
                  type="text"
                  value={editedArticle.author}
                  onChange={(e) => setEditedArticle({...editedArticle, author: e.target.value})}
                  placeholder="Nom de l'auteur"
                />
              </div>

              <div className="form-group">
                <label>Catégorie</label>
                <select
                  value={editedArticle.category}
                  onChange={(e) => setEditedArticle({...editedArticle, category: e.target.value})}
                >
                  <option value="actualite">Actualité</option>
                  <option value="conseil">Conseil</option>
                  <option value="guide">Guide</option>
                  <option value="info-consommateur">Info Consommateur</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Tags</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                {(editedArticle.tags || []).map((tag, idx) => (
                  <span key={idx} style={{ 
                    backgroundColor: '#e5e7eb', 
                    padding: '5px 10px', 
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Ajouter un tag..."
                />
                <button type="button" onClick={addTag} className="btn-add">
                  Ajouter
                </button>
              </div>
            </div>
          </div>

          <div className="form-actions-sticky">
            <button onClick={() => onSave(editedArticle)} className="btn btn-primary">
              💾 Enregistrer
            </button>
            <button onClick={onCancel} className="btn btn-secondary">
              ✕ Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant détails
const ArticleDetails = ({ article, onEdit, onPublish, onFeature, onDelete, formatDate }) => {
  if (!article) {
    return (
      <div className="card">
        <div className="card-header">
          <h2>Détails de l'article</h2>
        </div>
        <div className="details-content">
          <div className="empty-state">
            Sélectionnez un article pour voir les détails
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2>Détails de l'article</h2>
      </div>
      <div className="details-content">
        <div className="details">
          {article.image && (
            <div className="detail-item logo-section">
              <label>Image</label>
              <img src={article.image} alt={article.title} className="shop-logo-large" />
            </div>
          )}

          <div className="detail-item">
            <label>Titre</label>
            <p>{article.title}</p>
          </div>

          <div className="detail-item">
            <label>Slug</label>
            <p>/actualites/{article.slug}</p>
          </div>

          <div className="detail-item">
            <label>Chapeau</label>
            <p>{article.excerpt}</p>
          </div>

          <div className="detail-section">
            <label>Contenu</label>
            <div style={{ 
              backgroundColor: '#f9fafb', 
              padding: '20px', 
              borderRadius: '8px',
              maxHeight: '400px',
              overflow: 'auto'
            }}>
              <div dangerouslySetInnerHTML={{ __html: article.content }} />
            </div>
          </div>

          <div className="detail-item">
            <label>Auteur</label>
            <p>{article.author}</p>
          </div>

          <div className="detail-item">
            <label>Catégorie</label>
            <p>{article.category}</p>
          </div>

          {article.tags && article.tags.length > 0 && (
            <div className="detail-item">
              <label>Tags</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {article.tags.map((tag, idx) => (
                  <span key={idx} className="tag">{tag}</span>
                ))}
              </div>
            </div>
          )}

          <div className="detail-section">
            <label>Statistiques</label>
            <div className="stats-mini-grid">
              <div className="mini-stat">
                <div>Vues</div>
                <div className="value">{article.views}</div>
              </div>
              <div className="mini-stat">
                <div>Statut</div>
                <div className="value" style={{ textTransform: 'capitalize' }}>{article.status}</div>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <label>Dates</label>
            <div className="timeline">
              <div className="timeline-item">
                <span>Créé: {formatDate(article.createdAt)}</span>
              </div>
              {article.publishedAt && (
                <div className="timeline-item">
                  <span>Publié: {formatDate(article.publishedAt)}</span>
                </div>
              )}
              <div className="timeline-item">
                <span>Modifié: {formatDate(article.updatedAt)}</span>
              </div>
            </div>
          </div>

          {article.source === 'franceinfo' && (
            <div className="detail-item">
              <label>Source externe</label>
              <p>
                📰 France Info<br />
                {article.externalUrl && (
                  <a href={article.externalUrl} target="_blank" rel="noopener noreferrer">
                    Voir l'article original →
                  </a>
                )}
              </p>
            </div>
          )}

          <div className="actions">
            <button onClick={onEdit} className="btn btn-secondary full">
              <Edit2 className="icon" />
              Éditer
            </button>

            <button
              onClick={() => onPublish(article._id, article.status !== 'published')}
              className={`btn ${article.status === 'published' ? 'btn-secondary' : 'btn-success'} full`}
            >
              {article.status === 'published' ? (
                <><EyeOff className="icon" /> Dépublier</>
              ) : (
                <><Eye className="icon" /> Publier</>
              )}
            </button>

            <button
              onClick={() => onFeature(article._id)}
              className={`btn ${article.featured ? 'btn-secondary' : 'btn-primary'} full`}
            >
              <Star className="icon" />
              {article.featured ? 'Retirer de la une' : 'Mettre à la une'}
            </button>

            <button
              onClick={() => onDelete(article._id)}
              className="btn btn-danger full"
            >
              <Trash2 className="icon" />
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticlesManager;